import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { recordsService } from '../services/recordsService';
import type { VisitRecord, ClinicalTreatment } from '../types/records';

export interface SeanceAmounts {
  totalDue: number;
  totalPaid: number;
  remaining: number;
}

export interface UseSeanceReturn {
  /** The active (OPEN) séance for current patient, null if none */
  activeSeance: VisitRecord | null;
  /** All séances (visit records) for current patient, newest first */
  allSeances: VisitRecord[];
  /** Whether data is loading */
  loading: boolean;
  /** Treatments linked to the active séance */
  seanceTreatments: ClinicalTreatment[];
  /** Whether there's an open séance */
  isSeanceOpen: boolean;
  /** Financial summary of active séance */
  seanceAmounts: SeanceAmounts;
  /** Grand totals across ALL séances */
  grandTotals: SeanceAmounts;
  /** Start a new séance (auto-called on first act if none open) */
  startSeance: () => Promise<VisitRecord | null>;
  /** Close the active séance */
  closeSeance: (amountPaid?: number) => Promise<void>;
  /** Update payment amount for active séance */
  updatePayment: (amount: number) => Promise<void>;
  /** Refresh séances from server */
  refreshSeances: () => Promise<void>;
  /** Get the active visit_id (for linking treatments) */
  activeVisitId: string | null;
  /** Error message */
  error: string | null;
  clearError: () => void;
}

export function useSeance(patientId: string | null): UseSeanceReturn {
  const [activeSeance, setActiveSeance] = useState<VisitRecord | null>(null);
  const [allSeances, setAllSeances] = useState<VisitRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prevent double-fetch on mount
  const fetchedForRef = useRef<string | null>(null);

  // ── Load séances when patient changes ──
  const refreshSeances = useCallback(async () => {
    if (!patientId) {
      setActiveSeance(null);
      setAllSeances([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch both in parallel
      const [active, visits] = await Promise.all([
        recordsService.getActiveSeance(patientId),
        recordsService.getVisitRecords(patientId),
      ]);

      setActiveSeance(active);
      // Sort newest-first, pin OPEN ones at top
      const sorted = [...visits].sort((a, b) => {
        if (a.status === 'OPEN' && b.status !== 'OPEN') return -1;
        if (b.status === 'OPEN' && a.status !== 'OPEN') return 1;
        return new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime();
      });
      setAllSeances(sorted);
    } catch (err) {
      console.error('[useSeance] Error loading séances:', err);
      setError('Erreur lors du chargement des séances');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId && patientId !== fetchedForRef.current) {
      fetchedForRef.current = patientId;
      refreshSeances();
    }
    if (!patientId) {
      fetchedForRef.current = null;
      setActiveSeance(null);
      setAllSeances([]);
    }
  }, [patientId, refreshSeances]);

  // ── Derived: treatments in active séance ──
  const seanceTreatments = useMemo<ClinicalTreatment[]>(() => {
    return activeSeance?.treatments ?? [];
  }, [activeSeance]);

  // ── Derived: financial amounts ──
  const seanceAmounts = useMemo<SeanceAmounts>(() => {
    if (!activeSeance) return { totalDue: 0, totalPaid: 0, remaining: 0 };
    const due = Number(activeSeance.amount_due) || Number(activeSeance.total_cost) || 0;
    const paid = Number(activeSeance.amount_paid) || 0;
    return {
      totalDue: due,
      totalPaid: paid,
      remaining: Math.max(0, due - paid),
    };
  }, [activeSeance]);

  const grandTotals = useMemo<SeanceAmounts>(() => {
    let totalDue = 0;
    let totalPaid = 0;
    for (const s of allSeances) {
      totalDue += Number(s.amount_due) || Number(s.total_cost) || 0;
      totalPaid += Number(s.amount_paid) || 0;
    }
    return {
      totalDue,
      totalPaid,
      remaining: Math.max(0, totalDue - totalPaid),
    };
  }, [allSeances]);

  // ── Start a new séance ──
  const startSeance = useCallback(async (): Promise<VisitRecord | null> => {
    if (!patientId) return null;

    // If already open, return it
    if (activeSeance?.status === 'OPEN') return activeSeance;

    try {
      const newVisit = await recordsService.createVisitRecord({
        patient_id: patientId,
        visit_type: 'consultation',
        chief_complaint: '',
      });
      setActiveSeance(newVisit);
      setAllSeances(prev => [newVisit, ...prev]);
      return newVisit;
    } catch (err) {
      console.error('[useSeance] Error starting séance:', err);
      setError('Erreur lors de la création de la séance');
      return null;
    }
  }, [patientId, activeSeance]);

  // ── Close the active séance ──
  const closeSeance = useCallback(async (amountPaid?: number) => {
    if (!activeSeance) {
      setError('Aucune séance ouverte');
      return;
    }

    try {
      const closed = await recordsService.closeSeance(activeSeance.id, amountPaid);
      setActiveSeance(null);
      // Update in allSeances
      setAllSeances(prev =>
        prev.map(s => s.id === closed.id ? closed : s)
      );
    } catch (err) {
      console.error('[useSeance] Error closing séance:', err);
      setError('Erreur lors de la clôture de la séance');
    }
  }, [activeSeance]);

  // ── Update payment ──
  const updatePayment = useCallback(async (amount: number) => {
    if (!activeSeance) return;

    try {
      const updated = await recordsService.updateSeancePayment(activeSeance.id, amount);
      setActiveSeance(updated);
      setAllSeances(prev =>
        prev.map(s => s.id === updated.id ? updated : s)
      );
    } catch (err) {
      console.error('[useSeance] Error updating payment:', err);
      setError('Erreur lors de la mise à jour du paiement');
    }
  }, [activeSeance]);

  const clearError = useCallback(() => setError(null), []);

  return {
    activeSeance,
    allSeances,
    loading,
    seanceTreatments,
    isSeanceOpen: activeSeance?.status === 'OPEN',
    seanceAmounts,
    grandTotals,
    startSeance,
    closeSeance,
    updatePayment,
    refreshSeances,
    activeVisitId: activeSeance?.id ?? null,
    error,
    clearError,
  };
}
